/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Text,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useToast} from '../../hooks/use-toast'
import CouponApplied from '../coupon-applied'
import {useForm} from 'react-hook-form'
import {PlusIcon, MinusIcon} from '../custom-icons'
import PromoCodeFields from '../forms/promo-code-fields'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {useGiftCardActions} from '../../hooks/use-gift-card-actions'
import useShipToCountry from '../../hooks/use-ship-to-country'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

export const REMOVE_COUPON_CONFIRMATION_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'CONFIRM REMOVE PROMO CODE',
        id: 'confirmation_modal.remove_coupon.title.confirm_remove'
    }),
    confirmationMessage: defineMessage({
        defaultMessage: 'Are you sure you want to remove this promo code?',
        id: 'confirmation_modal.remove_coupon.message.sure_to_remove'
    }),
    primaryActionLabel: defineMessage({
        defaultMessage: 'Yes, remove promo',
        id: 'confirmation_modal.remove_coupon.action.yes'
    }),
    primaryActionAriaLabel: defineMessage({
        defaultMessage: 'Yes, remove promo',
        id: 'confirmation_modal.remove_coupon.assistive_msg.yes'
    }),
    alternateActionLabel: defineMessage({
        defaultMessage: 'No, keep promo',
        id: 'confirmation_modal.remove_coupon.action.no'
    }),
    alternateActionAriaLabel: defineMessage({
        defaultMessage: 'No, keep promo',
        id: 'confirmation_modal.remove_coupon.assistive_msg.no'
    }),
    onPrimaryAction: noop
}

/**
 * Custom hook `usePromoCode` provides functionality to apply and remove promotional codes
 * from a shopping basket within a React application. It leverages various hooks and
 * utilities to manage form state, display notifications, and interact with the basket API.
 *
 * @returns {Object} An object containing:
 * - `form`: The form state and methods for managing promo code input.
 * - `submitPromoCode`: A function to apply a promo code to the current basket. It accepts an object
 *   with a `code` property and attempts to apply the promo code, providing user feedback via toast notifications.
 * - `removePromoCode`: A function to remove a promo code from the current basket. It accepts a `couponItemId`
 *   and attempts to remove the promo code, providing user feedback via toast notifications.
 *
 * The hook utilizes:
 * - `useIntl`: For internationalization and formatting messages.
 * - `useShipToCountry`: To determine the shipping country context.
 * - `useCurrentBasket`: To access the current basket data.
 * - `useForm`: To manage the form state for promo code input.
 * - `useToast`: To display notifications to the user.
 * - `useShopperBasketsMutation`: For performing mutations on the basket, specifically adding and removing coupons.
 *
 * Error handling is implemented to provide user feedback when applying a promo code fails,
 * prompting the user to check the validity of the code.
 */
export const usePromoCode = () => {
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const {shipToCountry} = useShipToCountry()
    const form = useForm()
    const toast = useToast()
    const {getGiftCardDetails} = useGiftCardActions(basket)

    const applyPromoCodeMutation = useShopperBasketsMutation('addCouponToBasket')
    const removePromoCodeMutation = useShopperBasketsMutation('removeCouponFromBasket')

    const submitPromoCode = async ({code}) => {
        try {
            const result = await applyPromoCodeMutation.mutateAsync({
                parameters: {basketId: basket?.basketId, c_country: shipToCountry?.id},
                body: {
                    code
                }
            })
            form.reset({code: ''})
            const couponInBasket = result?.couponItems.find((c) => c.code === code)
            if (couponInBasket?.valid) {
                if (couponInBasket?.statusCode === 'no_applicable_promotion') {
                    toast({
                        title: formatMessage({
                                defaultMessage:
                                    'Coupon code "{code}" cannot currently be added to your cart.',
                                id: 'use_promocode.info.no_applicable_promo'
                            },
                            {
                                code: code
                            }
                        ),
                        status: 'error'
                    })
                } else {
                    toast({
                        title: formatMessage({
                            defaultMessage: 'Promotion applied',
                            id: 'use_promocode.info.promo_applied'
                        }),
                        status: 'success'
                    })
                }
            } else {
                throw new Error('Coupon is not applied')
            }
        } catch (e) {
            let isGiftCardCode = false

            try {
                await getGiftCardDetails(code)
                isGiftCardCode = true
            } catch (e) {
                /* empty */
            }

            form.setError(
                'code',
                {
                    type: 'manual',
                    message: isGiftCardCode
                        ? formatMessage({
                              defaultMessage:
                                  'The code you provided is a E-Gift Card code. You can use it during the checkout process.',
                              id: 'use_promocode.error.code_is_gift_card_code'
                          })
                        : formatMessage({
                              defaultMessage: 'Please provide a valid promo code',
                              id: 'use_promocode.error.check_the_code'
                          })
                },
                {shouldFocus: true}
            )
        }
    }

    const removePromoCode = async (couponItemId) => {
        removePromoCodeMutation.mutate(
            {
                parameters: {basketId: basket?.basketId, couponItemId, c_country: shipToCountry?.id}
            },
            {
                onSuccess: () => {
                    toast({
                        title: formatMessage({
                            defaultMessage: 'Promotion removed',
                            id: 'use_promocode.info.promo_removed'
                        }),
                        status: 'success'
                    })
                },
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error'
                    })
                }
            }
        )
    }

    return {form, submitPromoCode, removePromoCode}
}

/**
 * PromoCode component renders an accordion interface for users to enter and apply promo codes.
 *
 * This component utilizes the `Accordion` and `AccordionItem` components to provide a collapsible
 * section where users can input promo codes. It also displays any applied coupon items from the
 * current basket.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.form - The form object used for handling promo code input and submission.
 * @param {Function} props.submitPromoCode - The function to call when submitting the promo code.
 * @param {Object} props.itemProps - Additional properties to pass to the `AccordionItem` component.
 *
 * @returns {JSX.Element} The rendered PromoCode component.
 *
 * @example
 * <PromoCode
 *   form={formInstance}
 *   submitPromoCode={handlePromoCodeSubmit}
 *   itemProps={additionalItemProps}
 * />
 *
 * @component
 */
export const PromoCode = ({form, submitPromoCode, itemProps}) => {
    const [isOpen, setOpen] = useState(false)
    const {data: basket} = useCurrentBasket()

    useEffect(() => {
        if (basket.couponItems?.length > 0) {
            setOpen(true)
        }
    }, [])

    return (
        <Accordion
            allowToggle
            index={isOpen ? 0 : -1}
            onChange={() => setOpen(!isOpen)}
            borderBottomWidth="1px"
            borderColor="borderGray"
        >
            <AccordionItem
                {...itemProps}
                mx="-5px"
                sx={{'&:last-of-type': {borderBottomWidth: '0'}}}
            >
                {({isExpanded}) => (
                    <>
                        <AccordionButton
                            as={Button}
                            justifyContent="flex-start"
                            rightIcon={
                                isExpanded ? (
                                    <MinusIcon boxSize="12px" />
                                ) : (
                                    <PlusIcon boxSize="12px" />
                                )
                            }
                            mx="5px"
                            mb={isExpanded ? 0 : 10}
                            w="auto"
                            onClick={() => form.reset()}
                        >
                            <Text
                                variant="eyeBrowSmall"
                                textTransform="uppercase"
                                role="heading"
                                aria-level="2"
                            >
                                <FormattedMessage
                                    defaultMessage="HAVE A PROMO CODE?"
                                    id="promocode.accordion.button.have_promocode"
                                />
                            </Text>
                        </AccordionButton>

                        <AccordionPanel px={0} mb={12} py={10} mx="5px">
                            <Box
                                data-testid="promo-code-form"
                                as="form"
                                onSubmit={form.handleSubmit(submitPromoCode)}
                                noValidate
                            >
                                <PromoCodeFields form={form} />
                            </Box>
                            {basket.couponItems?.length > 0 && (
                                <Stack>
                                    {basket.couponItems.map((item) => (
                                        <CouponApplied item={item} key={item.couponItemId} />
                                    ))}
                                </Stack>
                            )}
                        </AccordionPanel>
                    </>
                )}
            </AccordionItem>
        </Accordion>
    )
}

PromoCode.propTypes = {
    /** The form object returned from `usePromoCode` hook */
    form: PropTypes.object.isRequired,

    /** The submit callback returned from `usePromoCode` hook */
    submitPromoCode: PropTypes.func.isRequired,

    /** Props applied to inner AccordionItem. Useful for style overrides. */
    itemProps: PropTypes.object
}

export default PromoCode
